import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Chip from "@mui/material/Chip";
import { useTheme, alpha, keyframes, styled } from "@mui/material";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface ChatProps {
  placeholder?: string;
  onSubmit?: (message: string, files?: File[]) => void;
  navigateToChat?: boolean;
  autoFocus?: boolean;
  rows?: number;
  maxWidth?: string | number;
  animationDelay?: number;
  sessionId?: string;
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Chat: React.FC<ChatProps> = ({
  placeholder = "Ask me anything about climate policy, sustainable development...",
  onSubmit,
  navigateToChat = true,
  autoFocus = false,
  rows = 4,
  maxWidth = "600px",
  animationDelay = 0,
  sessionId,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [isVisible, setIsVisible] = useState(animationDelay === 0);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (animationDelay > 0) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, animationDelay);
      return () => clearTimeout(timer);
    }
  }, [animationDelay]);

  useEffect(() => {
    if (isVisible && autoFocus && inputRef.current) {
      const focusTimer = setTimeout(() => {
        inputRef.current?.focus();
      }, 400); // Focus after animation completes
      return () => clearTimeout(focusTimer);
    }
  }, [isVisible, autoFocus]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!message.trim() && attachedFiles.length === 0) return;

    let finalMessage = message.trim();
    if (!finalMessage && attachedFiles.length > 0) {
        finalMessage = "Look at the image provided";
    }

    // Upload files if any and sessionId is provided
    if (attachedFiles.length > 0 && sessionId) {
      await uploadFiles(attachedFiles, sessionId);
    }

    if (onSubmit) {
      onSubmit(finalMessage, attachedFiles);
    }

    if (navigateToChat) {
      // Navigate to the chat page with the initial question
      navigate("/prompt", { state: { initialQuestion: finalMessage } });
    }

    setMessage("");
    setAttachedFiles([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files);
      setAttachedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (items) {
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 0) {
        setAttachedFiles((prev) => [...prev, ...files]);
      }
    }
  };

  const uploadFiles = async (files: File[], sessionId: string) => {
    const config = await import("../config.json");
    const baseUrl = config.api.chatbot.url;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("content", file);

        await fetch(`${baseUrl}/session/${sessionId}/upload`, {
          method: "POST",
          body: formData,
        });
        // Intentionally not catching errors as per requirement
      } catch (error) {
        // Silently continue - endpoint may not exist yet
      }
    }
  };

  if (!isVisible) {
    return <Box sx={{ height: rows * 24 + 56, width: "100%", maxWidth }} />;
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: "100%",
        maxWidth,
        animation: animationDelay > 0 ? `${fadeInUp} 0.5s ease-out forwards` : "none",
      }}
    >
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          position: "relative",
          borderRadius: "16px",
          background: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: "blur(10px)",
          border: `2px solid ${isDragging ? theme.palette.primary.main : alpha(theme.palette.divider, 0.2)}`,
          overflow: "hidden",
          transition: "border-color 0.3s ease, background-color 0.3s ease",
          bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : "transparent",
          "&:hover": {
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
          "&:focus-within": {
            borderColor: alpha(theme.palette.primary.main, 0.5),
          },
        }}
      >
        {/* Attached Files Display */}
        {attachedFiles.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              pb: 1,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            {attachedFiles.map((file, index) => {
              const isImage = file.type.startsWith("image/");
              const imageUrl = isImage ? URL.createObjectURL(file) : null;
              
              return isImage && imageUrl ? (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={file.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.9),
                      "&:hover": {
                        bgcolor: theme.palette.background.paper,
                      },
                      width: 20,
                      height: 20,
                      padding: 0,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ) : (
                <Chip
                  key={index}
                  icon={<AttachFileIcon />}
                  label={file.name}
                  onDelete={() => handleRemoveFile(index)}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{
                    maxWidth: "200px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              );
            })}
          </Box>
        )}

        <TextField
          inputRef={inputRef}
          multiline
          rows={rows}
          fullWidth
          placeholder={placeholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              p: 2,
              pb: 6,
              fontSize: "1rem",
              lineHeight: 1.6,
              fontFamily: "'Source Sans Pro', 'Segoe UI', sans-serif",
              "& textarea": {
                "&::placeholder": {
                  color: alpha(theme.palette.text.secondary, 0.6),
                  opacity: 1,
                },
              },
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            right: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <IconButton
            component="label"
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              },
            }}
          >
            <AddIcon sx={{ fontSize: 20 }} />
            <VisuallyHiddenInput
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
            />
          </IconButton>
          <IconButton
            type="submit"
            disabled={!message.trim() && attachedFiles.length === 0}
            sx={{
              bgcolor: (message.trim() || attachedFiles.length > 0)
                ? theme.palette.primary.main
                : alpha(theme.palette.action.disabled, 0.1),
              color: (message.trim() || attachedFiles.length > 0)
                ? theme.palette.primary.contrastText
                : theme.palette.action.disabled,
              width: 40,
              height: 40,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: (message.trim() || attachedFiles.length > 0)
                  ? theme.palette.primary.dark
                  : alpha(theme.palette.action.disabled, 0.1),
                transform: (message.trim() || attachedFiles.length > 0) ? "scale(1.05)" : "none",
              },
              "&:disabled": {
                bgcolor: alpha(theme.palette.action.disabled, 0.1),
                color: theme.palette.action.disabled,
              },
            }}
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default Chat;
